import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCalendarRange } from '@fortawesome/pro-regular-svg-icons/faCalendarRange';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Flex, Form, Space } from 'antd';
import dayjs from 'dayjs';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { createFormFields } from 'pos-client/components/shared/Form/FormUtils.ts';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import {
  PosTimePicker,
  type PosTimePickerProps,
} from 'pos-client/components/shared/Form/PosTimePicker.tsx';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { TradingTimesRefinedFormSchema } from 'pos-client/schemas/SchedulingRefinedSchema.ts';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type TradingTimesFormModel,
  type TradingTimesModel,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

import { useWeekdays } from './useWeekdays.tsx';

interface ModalProps {
  data?: Partial<TradingTimesModel> | undefined;
  handleOk: () => void;
  handleCancel: () => void;
}

export const CreateEditTradingTimesModal = ({
  data,
  handleOk,
  handleCancel,
}: ModalProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const mode = data ? 'edit' : 'create';
  const weekdays = useWeekdays();

  const { showSuccess, showError } = useNotifications();

  const { getAllTradingTimes } = trpc.useContext();

  const form = useForm<TradingTimesFormModel>({
    mode: 'all',
    shouldFocusError: true,
    defaultValues: {
      ...(data && {
        dayOfWeek: data.dayOfWeek,
        opening: dayjs(data.opening, 'HH:mm:ss'),
        closing: dayjs(data.closing, 'HH:mm:ss'),
      }),
    },
    resolver: zodResolver(TradingTimesRefinedFormSchema),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = form;

  const [enableSubmit, setEnableSubmit] = useState(false);

  const modalTitle =
    mode === 'create'
      ? t('locations.trading.createTradingTimes')
      : t('locations.trading.updateTradingTime');

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleCancel,
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    await getAllTradingTimes.invalidate();
    await handleOk();
  };

  const { mutate: createTradingTimes, isLoading: isCreating } =
    trpc.createTradingTimes.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'created',
            entity: 'trading times',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'create',
            entity: 'trading times',
          })
        );
      },
    });
  const { mutate: updateTradingTimes, isLoading: isUpdating } =
    trpc.updateTradingTimes.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'trading times',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'trading times',
          })
        );
      },
    });
  const { mutate: deleteTradingTimes, isLoading: isDeleting } =
    trpc.deleteTradingTimes.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'trading times',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'trading times',
          })
        );
      },
    });

  const saveTradingTimes = handleSubmit((formData) => {
    const formattedData = {
      ...formData,
      opening: formData.opening.format('HH:mm:ss'),
      closing: formData.closing.format('HH:mm:ss'),
    };

    if (mode === 'edit') {
      updateTradingTimes({
        openingHoursId: id ?? '',
        tradingTimesId: data?.id ?? '',
        data: {
          id: data?.id ?? '',
          ...formattedData,
        },
      });
    } else {
      createTradingTimes({ openingHoursId: id || '', data: formattedData });
    }
  });

  const handleDelete = () => {
    deleteTradingTimes({
      openingHoursId: id || '',
      tradingTimesId: data?.id || '',
    });
  };

  const { showModal: handleRemoveTradingTimes } = useConfirmModal({
    type: 'delete',
    title: t('locations.trading.removeTradingTime'),
    content: t('locations.trading.removeTradingTimeConfirm'),
    confirmText: t('common.buttons.delete'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleDelete,
  });

  const resetAll = () => {
    reset({
      dayOfWeek: '',
      opening: '',
      closing: '',
    });
  };

  const defaultTimePickerProps: Partial<PosTimePickerProps> = {
    use12Hours: true,
    showNow: false,
    needConfirm: true,
    style: fullWidth,
    allowClear: false,
  };

  useEffect(() => {
    const selectedValue = watch((value) => {
      if (typeof value !== 'undefined') {
        const hasDayOfWeek = value?.dayOfWeek;
        const hasDateRange = value?.opening && value?.closing;
        setEnableSubmit(!!(hasDayOfWeek && hasDateRange));
      }
    });
    return () => selectedValue.unsubscribe();
  }, [watch]);

  return (
    <PosModal
      header={{ title: modalTitle, icon: faCalendarRange }}
      open
      onCancel={onCancelModal}
      afterClose={resetAll}
      footer={
        <Flex justify="space-between" style={fullWidth}>
          <Button
            danger
            onClick={() => handleRemoveTradingTimes()}
            loading={isDeleting}
            hidden={mode === 'create'}
          >
            {t('common.buttons.delete')}
          </Button>
          <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
            <Button type="default" onClick={onCancelModal}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={saveTradingTimes}
              loading={isUpdating || isCreating}
              disabled={!isDirty || !enableSubmit}
            >
              {mode === 'edit'
                ? t('common.buttons.update')
                : t('common.buttons.create')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form layout="vertical" fields={createFormFields(form)}>
        <PosGrid>
          <PosColumn span={24}>
            <PosSelect
              control={control}
              name="dayOfWeek"
              label={t('common.time.unit.day')}
              options={weekdays}
              showSearch
            />
          </PosColumn>
          <PosColumn span={24}>
            <PosTimePicker
              control={control}
              name="opening"
              label={t('locations.trading.openingTime')}
              {...defaultTimePickerProps}
            />
          </PosColumn>
          <PosColumn span={24}>
            <PosTimePicker
              control={control}
              name="closing"
              label={t('locations.trading.closeTime')}
              {...defaultTimePickerProps}
            />
          </PosColumn>
        </PosGrid>
      </Form>
    </PosModal>
  );
};
